import React, { forwardRef } from "react";
import { Link as RouterLink, LinkProps as RouterLinkProps } from "react-router-dom";
import { Link as MuiLink, LinkProps as MuiLinkProps } from "@mui/material";
import { useNavigation } from "../../hooks/useNavigation";

/**
 * Props for the NavigationLink component.
 * Extends MUI Link props and adds navigation-specific properties.
 */
export interface NavigationLinkProps extends Omit<MuiLinkProps, "href"> {
  /**
   * The target path (without lltsBranchId). The component will automatically
   * preserve the lltsBranchId parameter when building the final href.
   */
  to: string;

  /**
   * Whether to use React Router Link for internal navigation (default: true)
   * or regular anchor tag for external links (false).
   */
  internal?: boolean;

  /**
   * Whether to replace the current history entry instead of pushing a new one.
   * Only applies when internal=true.
   */
  replace?: boolean;
}

/**
 * A Link component that automatically preserves the lltsBranchId parameter.
 * This component can be used for both internal navigation (using React Router)
 * and external links (using regular anchor tags).
 *
 * For internal navigation, it uses React Router's Link component under the hood
 * and automatically builds the correct path with lltsBranchId preserved.
 *
 * For external links, it uses a regular anchor tag with the href attribute.
 */
export const NavigationLink = forwardRef<HTMLAnchorElement, NavigationLinkProps>(
  ({ to, internal = true, replace = false, onClick, children, ...muiLinkProps }, ref) => {
    const { buildPath, navigateTo } = useNavigation();

    // Check if the link is external (starts with http/https)
    const isExternal = /^https?:\/\//i.test(to);
    const shouldUseInternal = internal && !isExternal;

    // Build the complete path with lltsBranchId preserved for internal links
    const href = shouldUseInternal ? buildPath(to) : to;

    // Handle click events for internal navigation
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Call the original onClick handler if provided
      if (onClick) {
        onClick(event);
      }

      // For internal navigation, prevent default and use our navigation utility
      if (shouldUseInternal && !event.defaultPrevented) {
        event.preventDefault();
        navigateTo(to, replace);
      }
    };

    if (shouldUseInternal) {
      // For internal navigation, use React Router Link with MUI Link styling
      const routerLinkProps = { to: href, ...(replace && { replace }) };
      return (
        <MuiLink {...muiLinkProps} component={RouterLink} {...routerLinkProps} onClick={handleClick} ref={ref}>
          {children}
        </MuiLink>
      );
    }

    // For external links, use regular MUI Link with anchor tag
    return (
      <MuiLink {...muiLinkProps} href={href} onClick={handleClick} ref={ref}>
        {children}
      </MuiLink>
    );
  }
);

NavigationLink.displayName = "NavigationLink";

/**
 * A simple router link component that preserves lltsBranchId.
 * This is useful when you need just the routing functionality without MUI styling.
 */
export interface SimpleNavigationLinkProps extends Omit<RouterLinkProps, "to"> {
  /**
   * The target path (without lltsBranchId). The component will automatically
   * preserve the lltsBranchId parameter when building the final path.
   */
  to: string;
}

export const SimpleNavigationLink = forwardRef<any, SimpleNavigationLinkProps>(({ to, ...routerLinkProps }, ref) => {
  const { buildPath } = useNavigation();

  // Check if the link is external (starts with http/https)
  const isExternal = /^https?:\/\//i.test(to);

  if (isExternal) {
    // For external links, use a regular anchor tag
    return <a {...routerLinkProps} href={to} ref={ref} />;
  }

  // For internal links, use React Router Link with branch-aware path
  const href = buildPath(to);
  return <RouterLink {...routerLinkProps} to={href} ref={ref} />;
});

SimpleNavigationLink.displayName = "SimpleNavigationLink";
